import { AdditionalFees_Fee as AdditionalFeesFeePB } from "gen/company/models_pb"
import { AcknowledgeCode } from "gen/integrations/x12/models_pb"
import { Product as ProductPB } from "gen/product/models_pb"
import { Address as AddressPB } from "gen/proto/address/models_pb"
import {
  Order_Method as OrderMethodPB,
  Order_Product as OrderProductPB,
  Order_Status as OrderStatusPB,
  OrderShipment_Status as OrderShipmentStatusPB,
} from "gen/txn/models_pb"

import { Decimal } from "../../classes/decimal"
import { Money } from "../../classes/money"
import { UUID } from "../../classes/uuid"
import { isKeyOf } from "../../utils/"

interface Base {
  id: UUID
  status: BaseOrderStatus | OrderShipmentStatus
  identifier: string
  vendorName: string
  locationName: string
}

interface OrderOrderBase extends Base {
  status: BaseOrderStatus
  createdBy: string
  createdAt: Date
  orderedBy?: string
  orderedAt?: Date
  voidedBy?: string
  voidedAt?: Date
}

interface OrderShipmentBase extends Base {
  status: OrderShipmentStatus
  receivedAt: Date
  receivedBy: string
  paidAt?: Date
  paidBy?: string
  voucheredAt?: Date
  voucheredBy?: string
}

export interface OrderSearchResults extends OrderOrderBase {
  orderType: "order"
  totalPrice: Money
  productCount: Decimal
}

export interface OrderShipmentSearchResults extends OrderShipmentBase {
  orderType: "orderShipment"
  totalPrice: Money
  productCount: Decimal
}

export interface Order extends OrderOrderBase {
  vendorId: UUID
  locationId: UUID
  locationPrefix: string
  productsPB: OrderProductPB[]
  orderNotes?: string
  requestedDeliveryAt?: Date
  dropshipAddressPB?: AddressPB
  dropshipRecipient?: string
  promoCode?: string
  productsById: Record<string, ProductPB>
  orderMethodPB: OrderMethodPB
  additionalFeesByProductId: Record<string, AdditionalFeesFeePB[]>
  acknowledgment: AcknowledgeCode
  hasFunctionalAck: boolean
}
export interface OrderShipment extends OrderShipmentBase {
  orderId: UUID
  vendorId: UUID
  locationId: UUID
  orderShipmentProducts: OrderShipmentProduct[]
  shipmentNotes?: string
  subtotal: Money
  total: Money
}

export interface OrderShipmentProduct {
  id: UUID
  identifier: string
  name: string
  quantityOrdered: Decimal
  quantityReceived: Decimal
  cost: Money
  subtotal: Money
  productNotes?: string
}

export const baseOrderStatuses = {
  [OrderStatusPB.UNDEFINED]: "UndefinedOrder",
  [OrderStatusPB.CREATED]: "Drafts",
  [OrderStatusPB.ORDERED]: "Ordered",
  [OrderStatusPB.VOIDED]: "Voided",
  [OrderStatusPB.RECEIVED]: "ReceivedOrder",
} as const

export type BaseOrderStatus =
  (typeof baseOrderStatuses)[keyof typeof baseOrderStatuses]

export const orderShipmentStatuses = {
  [OrderShipmentStatusPB.UNDEFINED]: "UndefinedShipment",
  [OrderShipmentStatusPB.RECEIVED]: "Received",
  [OrderShipmentStatusPB.VOUCHERED]: "Vouchered",
  [OrderShipmentStatusPB.PAID]: "Paid",
} as const

export type OrderShipmentStatus =
  (typeof orderShipmentStatuses)[keyof typeof orderShipmentStatuses]

export type OrderStatus =
  | { statusType: "order"; status: BaseOrderStatus }
  | { statusType: "orderShipment"; status: OrderShipmentStatus }

export type OrderStatusCount = {
  status: OrderStatus
  count: number
}

export const orderBaseStatusToPBOrderBaseStatus = {
  UndefinedOrder: OrderStatusPB.UNDEFINED,
  Drafts: OrderStatusPB.CREATED,
  Ordered: OrderStatusPB.ORDERED,
  Voided: OrderStatusPB.VOIDED,
  ReceivedOrder: OrderStatusPB.RECEIVED,
} as const satisfies Record<BaseOrderStatus, OrderStatusPB>

export const orderShipmentStatusToPBShipmentStatus = {
  UndefinedShipment: OrderShipmentStatusPB.UNDEFINED,
  Received: OrderShipmentStatusPB.RECEIVED,
  Vouchered: OrderShipmentStatusPB.VOUCHERED,
  Paid: OrderShipmentStatusPB.PAID,
} as const satisfies Record<OrderShipmentStatus, OrderShipmentStatusPB>

export function pbOrderStatusToBaseOrderStatus(
  status: OrderStatusPB
): BaseOrderStatus {
  return baseOrderStatuses[status]
}

export function pbShipmentStatusToOrderShipmentStatus(
  status: OrderShipmentStatusPB
): OrderShipmentStatus {
  return orderShipmentStatuses[status]
}

export function stringToOrderStatus(status: string): OrderStatus | undefined {
  if (isKeyOf(status, orderBaseStatusToPBOrderBaseStatus))
    return { statusType: "order", status }

  if (isKeyOf(status, orderShipmentStatusToPBShipmentStatus))
    return { statusType: "orderShipment", status }

  return undefined
}
